<template>
  <div
    ref="host"
    class="commander"
    :class="{ live }"
    :style="{ width: widthPx + 'px' }"
  >
    <canvas ref="canvas" @click="onClick"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { COMMANDER_BBL_PER_INCH, COMMANDER_GLASS_INCHES, COMMANDER_LABELS, COMMANDER_WEIR_INCHES, commanderVisualBays } from './fracTank'

const TRANSFER_LOW_IN = 19
const TRANSFER_HIGH_IN = 50
const TRANSFER_CYCLE_MS = 18000

const props = withDefaults(defineProps<{
  name?: string
  spillInches?: number
  oilInches?: number
  waterInches?: number
  capacityBbl?: number
  live?: boolean
  widthPx?: number
  fillToken?: string | number
  bays?: any[]
  selectedIndex?: number
}>(), {
  name: 'Sand Commander',
  spillInches: COMMANDER_GLASS_INCHES,
  oilInches: 0,
  waterInches: 0,
  capacityBbl: 110,
  live: false,
  widthPx: 560,
  selectedIndex: -1,
})

const emit = defineEmits<{
  select: [index: number]
}>()

const host = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
let raf = 0
let visible = true
let reduced = false
let start = performance.now()
let fillStart = performance.now()
let replay = true
let lastToken = props.fillToken
const shown = [0, 0, 0, 0].map(() => ({ water: 0, oil: 0 }))
let bayHits: Array<{ i: number; x: number; y: number; w: number; h: number }> = []

function transferWave(now: number) {
  const t = ((now - start) % TRANSFER_CYCLE_MS) / TRANSFER_CYCLE_MS
  if (t < 0.36) return { inches: TRANSFER_LOW_IN + (TRANSFER_HIGH_IN - TRANSFER_LOW_IN) * ease(t / 0.36), mode: 'fill' as const }
  if (t < 0.58) return { inches: TRANSFER_HIGH_IN, mode: 'rate' as const }
  if (t < 0.92) return { inches: TRANSFER_HIGH_IN - (TRANSFER_HIGH_IN - TRANSFER_LOW_IN) * ease((t - 0.58) / 0.34), mode: 'transfer' as const }
  return { inches: TRANSFER_LOW_IN, mode: 'low' as const }
}

function targets(now = performance.now()) {
  const glass = COMMANDER_GLASS_INCHES
  const fromBays = props.bays && props.bays.length
    ? commanderVisualBays({ bays: props.bays })
    : COMMANDER_LABELS.map((name, i) => ({
      name,
      waterInches: i < 3 ? COMMANDER_WEIR_INCHES : Math.max(0, props.waterInches),
      oilInches: i < 3 ? 0 : Math.max(0, props.oilInches),
      strap: i === 3,
    }))
  return fromBays.map((bay, i) => {
    const waterIn = Math.max(0, Number(bay.waterInches) || 0)
    const oilIn = Math.max(0, Number(bay.oilInches) || 0)
    const stored = waterIn + oilIn
    const pumping = i === 3 && stored < 0.75
    const wave = pumping ? transferWave(now) : null
    const showIn = pumping ? (reduced ? TRANSFER_LOW_IN : wave!.inches) : stored
    const showWater = pumping ? showIn : waterIn
    const showOil = pumping ? 0 : oilIn
    return {
      water: Math.min(1, showWater / glass),
      oil: Math.min(1 - Math.min(1, showWater / glass), showOil / glass),
      name: bay.name || COMMANDER_LABELS[i],
      inches: stored,
      showInches: showIn,
      strap: i === 3,
      pumping,
      mode: wave?.mode || null,
    }
  })
}

function ease(t: number) {
  const u = Math.min(1, Math.max(0, t))
  return 1 - (1 - u) ** 3
}

function tick(now: number) {
  const el = canvas.value
  if (!el) return
  const ctx = el.getContext('2d')
  if (!ctx) return
  const dpr = window.devicePixelRatio || 1
  const cssW = el.clientWidth || props.widthPx
  const cssH = el.clientHeight || Math.round(props.widthPx / 2.05)
  if (el.width !== Math.round(cssW * dpr) || el.height !== Math.round(cssH * dpr)) {
    el.width = Math.round(cssW * dpr)
    el.height = Math.round(cssH * dpr)
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)

  const want = targets(now)
  while (shown.length < 4) shown.push({ water: 0, oil: 0 })
  const p = reduced ? 1 : replay ? ease((now - fillStart) / 1200) : 1
  if (p >= 1) replay = false
  want.forEach((t, i) => {
    if (t.pumping) {
      shown[i].water = t.water
      shown[i].oil = 0
      return
    }
    if (reduced || !replay) {
      if (props.live) {
        shown[i].water += (t.water - shown[i].water) * 0.22
        shown[i].oil += (t.oil - shown[i].oil) * 0.22
      } else {
        shown[i].water = t.water
        shown[i].oil = t.oil
      }
    } else {
      shown[i].water = t.water * p
      shown[i].oil = t.oil * p
    }
  })
  const phase = reduced ? 0 : ((now - start) / 2800) * Math.PI * 2
  drawUnit(ctx, cssW, cssH, want, phase)
  raf = visible ? requestAnimationFrame(tick) : 0
}

function tan(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  const g = ctx.createLinearGradient(x, 0, x + w, 0)
  g.addColorStop(0, '#9E8D72')
  g.addColorStop(0.18, '#C8B89A')
  g.addColorStop(0.45, '#D6C7AB')
  g.addColorStop(0.7, '#B9A888')
  g.addColorStop(1, '#8F7E64')
  ctx.fillStyle = g
  ctx.fillRect(x, y, w, h)
}

function ribs(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, w, h)
  ctx.clip()
  ctx.strokeStyle = 'rgba(70, 58, 42, 0.22)'
  ctx.lineWidth = 1
  for (let i = 0; i < 28; i++) {
    const rx = x + 3 + i * (w / 27)
    ctx.beginPath()
    ctx.moveTo(rx, y)
    ctx.lineTo(rx, y + h)
    ctx.stroke()
  }
  ctx.restore()
}

function waveY(x: number, width: number, surfaceY: number, phase: number) {
  return surfaceY + Math.sin((x / Math.max(1, width)) * 2.1 * Math.PI * 2 + phase) * 1.4
}

function drawFluids(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  waterR: number,
  oilR: number,
  phase: number,
  selected: boolean,
) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, w, h)
  ctx.clip()
  ctx.fillStyle = selected ? '#24313B' : '#1B252D'
  ctx.fillRect(x, y, w, h)
  const waterTop = y + h * (1 - waterR)
  const oilTop = y + h * (1 - waterR - oilR)
  if (waterR > 0.001) {
    const g = ctx.createLinearGradient(0, waterTop, 0, y + h)
    g.addColorStop(0, '#7EE9FF')
    g.addColorStop(0.45, '#1784B8')
    g.addColorStop(1, '#0B4F78')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(x, y + h)
    for (let px = x; px <= x + w; px += 2) ctx.lineTo(px, waveY(px - x, w, waterTop, phase))
    ctx.lineTo(x + w, y + h)
    ctx.closePath()
    ctx.fill()
  }
  if (oilR > 0.001) {
    const g = ctx.createLinearGradient(0, oilTop, 0, waterTop)
    g.addColorStop(0, '#E8B45A')
    g.addColorStop(0.5, '#C48432')
    g.addColorStop(1, '#8A5A16')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(x, oilTop)
    for (let px = x; px <= x + w; px += 2) ctx.lineTo(px, waveY(px - x, w, oilTop, phase * 0.6))
    for (let px = x + w; px >= x; px -= 2) ctx.lineTo(px, waveY(px - x, w, waterTop, phase))
    ctx.closePath()
    ctx.fill()
  }
  ctx.strokeStyle = '#E24A4A'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(x + 2, y + 2)
  ctx.lineTo(x + w - 2, y + 2)
  ctx.stroke()
  if (selected) {
    ctx.strokeStyle = '#5EEAD4'
    ctx.lineWidth = 2
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2)
  }
  ctx.restore()
}

function drawSiteGlass(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  h: number,
  waterR: number,
  oilR: number,
) {
  const spill = COMMANDER_GLASS_INCHES
  const tubeW = Math.max(6, h * 0.07)
  const rulerW = Math.max(14, h * 0.18)
  const fit = 4
  const tubeX = x
  const tubeY = y + fit
  const tubeH = h - fit * 2
  const fill = Math.min(1, Math.max(0, waterR + oilR))

  ctx.fillStyle = '#8A7A62'
  ctx.fillRect(tubeX - 2, y, tubeW + 4, fit)
  ctx.fillRect(tubeX - 2, y + h - fit, tubeW + 4, fit)

  ctx.fillStyle = 'rgba(210, 232, 242, 0.22)'
  ctx.fillRect(tubeX, tubeY, tubeW, tubeH)
  ctx.save()
  ctx.beginPath()
  ctx.rect(tubeX, tubeY, tubeW, tubeH)
  ctx.clip()
  if (waterR > 0.001) {
    const waterTop = tubeY + tubeH * (1 - waterR)
    const g = ctx.createLinearGradient(0, waterTop, 0, tubeY + tubeH)
    g.addColorStop(0, '#8EEBFF')
    g.addColorStop(1, '#0C5A86')
    ctx.fillStyle = g
    ctx.fillRect(tubeX, waterTop, tubeW, tubeY + tubeH - waterTop)
  }
  if (oilR > 0.001) {
    const oilTop = tubeY + tubeH * (1 - fill)
    const waterTop = tubeY + tubeH * (1 - waterR)
    const g = ctx.createLinearGradient(0, oilTop, 0, waterTop)
    g.addColorStop(0, '#E8B45A')
    g.addColorStop(1, '#8A5A16')
    ctx.fillStyle = g
    ctx.fillRect(tubeX, oilTop, tubeW, Math.max(1, waterTop - oilTop))
  }
  ctx.fillStyle = 'rgba(255,255,255,0.38)'
  ctx.fillRect(tubeX + 1, tubeY, 1.6, tubeH)
  ctx.restore()
  ctx.strokeStyle = 'rgba(230, 246, 252, 0.85)'
  ctx.lineWidth = 1
  ctx.strokeRect(tubeX + 0.5, tubeY + 0.5, tubeW - 1, tubeH - 1)

  const rulerX = tubeX + tubeW + 2
  ctx.fillStyle = 'rgba(248, 244, 236, 0.92)'
  ctx.fillRect(rulerX, tubeY, rulerW, tubeH)
  ctx.strokeStyle = 'rgba(90, 78, 62, 0.35)'
  ctx.strokeRect(rulerX, tubeY, rulerW, tubeH)
  ctx.fillStyle = '#2A241C'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.font = `700 ${Math.max(6.5, h * 0.075)}px Inter, system-ui, sans-serif`
  const marks = [12, 24, 36, 48, 60, 69]
  for (let inch = 6; inch <= spill; inch += 6) {
    const t = inch / spill
    const my = tubeY + tubeH * (1 - t)
    const major = marks.includes(inch)
    ctx.beginPath()
    ctx.moveTo(rulerX, my)
    ctx.lineTo(rulerX + (major ? rulerW * 0.42 : rulerW * 0.22), my)
    ctx.stroke()
    if (major) ctx.fillText(String(inch), rulerX + rulerW - 1.5, my)
  }
  ctx.fillText('1', rulerX + rulerW - 1.5, tubeY + tubeH - 1)

  if (fill > 0.01) {
    const markY = tubeY + tubeH * (1 - fill)
    ctx.strokeStyle = '#D21F2A'
    ctx.lineWidth = 1.6
    ctx.beginPath()
    ctx.moveTo(tubeX - 1, markY)
    ctx.lineTo(tubeX + tubeW + 1, markY)
    ctx.stroke()
  }
}

function drawUnit(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  want: Array<{ water: number; oil: number; name: string; cap?: number; inches?: number; showInches?: number; strap?: boolean; pumping?: boolean; mode?: string | null }>,
  phase: number,
) {
  const groundY = h * 0.90
  ctx.fillStyle = '#2A241C'
  ctx.fillRect(0, groundY, w, h - groundY)

  const bodyX = w * 0.205
  const bodyW = w * 0.755
  const bodyY = h * 0.30
  const bodyH = h * 0.52
  const towerW = w * 0.145
  const towerY = h * 0.175

  // shadow
  ctx.fillStyle = 'rgba(0,0,0,0.28)'
  ctx.beginPath()
  ctx.ellipse(w * 0.58, groundY + 2, bodyW * 0.48, 6, 0, 0, Math.PI * 2)
  ctx.fill()

  // auger
  ctx.strokeStyle = '#B7A88C'
  ctx.lineWidth = Math.max(7, w * 0.016)
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(w * 0.015, h * 0.22)
  ctx.lineTo(w * 0.205, h * 0.50)
  ctx.stroke()
  ctx.strokeStyle = '#8F8068'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(w * 0.03, h * 0.235)
  ctx.lineTo(w * 0.195, h * 0.49)
  ctx.stroke()
  ctx.fillStyle = '#F4F1EA'
  ctx.beginPath()
  ctx.ellipse(w * 0.02, h * 0.30, 5, 11, 0.15, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = '#C9C3B7'
  ctx.stroke()

  // mast
  ctx.strokeStyle = '#B7A88C'
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(w * 0.188, h * 0.12)
  ctx.lineTo(w * 0.188, bodyY + 8)
  ctx.moveTo(w * 0.188, h * 0.12)
  ctx.lineTo(w * 0.188 + 16, h * 0.12)
  ctx.stroke()

  // vent pot
  ctx.fillStyle = '#C2B396'
  ctx.fillRect(w * 0.248, towerY - 10, 14, 18)
  ctx.beginPath()
  ctx.arc(w * 0.255 + 7, towerY - 10, 7, Math.PI, 0)
  ctx.fill()
  ctx.fillRect(w * 0.258, towerY - 22, 4, 12)

  // small pot
  ctx.beginPath()
  ctx.ellipse(w * 0.40, bodyY - 4, 10, 4, 0, 0, Math.PI * 2)
  ctx.fillStyle = '#B7A88C'
  ctx.fill()
  ctx.fillRect(w * 0.392, bodyY - 14, 3, 10)

  // tower / left step
  tan(ctx, bodyX, towerY, towerW, bodyY + bodyH - towerY)
  ribs(ctx, bodyX, towerY, towerW, bodyY + bodyH - towerY)

  // main box
  tan(ctx, bodyX + towerW - 1, bodyY, bodyW - towerW + 1, bodyH)
  ribs(ctx, bodyX + towerW - 1, bodyY, bodyW - towerW + 1, bodyH)

  // top cap
  ctx.fillStyle = '#D2C4A8'
  ctx.fillRect(bodyX, towerY - 3, towerW, 5)
  ctx.fillRect(bodyX + towerW - 1, bodyY - 3, bodyW - towerW + 1, 5)

  // yellow rail
  const railX = bodyX + bodyW * 0.42
  const railY = bodyY - h * 0.105
  ctx.strokeStyle = '#E6D24A'
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.moveTo(railX, bodyY)
  ctx.lineTo(railX, railY)
  ctx.lineTo(bodyX + bodyW - 6, railY)
  ctx.lineTo(bodyX + bodyW - 6, bodyY)
  ctx.stroke()
  ctx.lineWidth = 1.4
  ctx.beginPath()
  for (let i = 0; i < 7; i++) {
    const x = railX + ((bodyX + bodyW - 6 - railX) * i) / 6
    ctx.moveTo(x, railY)
    ctx.lineTo(x, bodyY - 2)
  }
  ctx.moveTo(railX, railY + (bodyY - railY) * 0.55)
  ctx.lineTo(bodyX + bodyW - 6, railY + (bodyY - railY) * 0.55)
  ctx.stroke()

  // name plate
  ctx.fillStyle = '#161616'
  const plateW = Math.max(86, w * 0.16)
  const plateH = Math.max(16, h * 0.055)
  const plateX = bodyX + 10
  const plateY = towerY + 18
  ctx.fillRect(plateX, plateY, plateW, plateH)
  ctx.fillStyle = '#F3EDE3'
  ctx.font = `800 ${Math.max(8, w * 0.018)}px Inter, system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('SAND', plateX + plateW / 2, plateY + plateH * 0.32)
  ctx.fillText('COMMANDER', plateX + plateW / 2, plateY + plateH * 0.70)

  // 4 bay windows + a site glass (clear tube + 1–69 in ruler) on each section
  const winX = bodyX + towerW + 8
  const winW = bodyW - towerW - 14
  const winY = bodyY + bodyH * 0.14
  const winH = bodyH * 0.72
  const gap = 5
  const glassW = Math.max(26, w * 0.048)
  const cellW = (winW - gap * 3) / 4
  const fluidW = Math.max(22, cellW - glassW - 3)
  bayHits = []
  for (let i = 0; i < 4; i++) {
    const x = winX + i * (cellW + gap)
    const t = want[i] || { water: 0, oil: 0, name: '', cap: 27.5, strap: false }
    const s = shown[i] || { water: 0, oil: 0 }
    drawFluids(ctx, x, winY, fluidW, winH, s.water, s.oil, phase + i * 0.4, i === props.selectedIndex || !!t.strap && props.selectedIndex < 0)
    ctx.strokeStyle = '#7A6B55'
    ctx.lineWidth = 2
    ctx.strokeRect(x, winY, fluidW, winH)
    if (i < 3) {
      const weirX = x + fluidW - 1
      const weirY = winY + winH * (1 - COMMANDER_WEIR_INCHES / COMMANDER_GLASS_INCHES)
      ctx.fillStyle = '#C8B89A'
      ctx.fillRect(weirX - 2, weirY, 4, 7)
      const spilling = (shown[i]?.water || 0) + (shown[i]?.oil || 0) >= COMMANDER_WEIR_INCHES / COMMANDER_GLASS_INCHES - 0.02
      if (spilling) {
        ctx.strokeStyle = 'rgba(110, 200, 230, 0.75)'
        ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.moveTo(weirX + 2, weirY + 6)
        ctx.quadraticCurveTo(weirX + 7, weirY + 14, weirX + 5, winY + winH * 0.22)
        ctx.stroke()
      }
    }
    drawSiteGlass(ctx, x + fluidW + 2, winY, winH, s.water, s.oil)
    ctx.fillStyle = '#F3EDE3'
    ctx.font = `800 ${Math.max(8, w * 0.018)}px Inter, system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.fillText(t.name || `${i + 1}`, x + fluidW / 2, winY - 6)
    if (t.pumping) {
      ctx.fillStyle = t.mode === 'rate' ? '#E7C36A' : '#7DD3FC'
      ctx.font = `700 ${Math.max(7, w * 0.014)}px Inter, system-ui, sans-serif`
      ctx.fillText(
        t.mode === 'rate' ? 'RATE' : t.mode === 'fill' ? 'FILLING' : 'TRANSFER',
        x + fluidW / 2,
        winY + winH + 10,
      )
    }
    bayHits.push({ i, x, y: winY, w: cellW, h: winH })
  }

  // black base / frame
  ctx.fillStyle = '#1A1A1A'
  ctx.fillRect(bodyX - 2, bodyY + bodyH - 5, bodyW + 4, 8)

  // wheel
  const wx = w * 0.175
  const wy = groundY - 8
  ctx.fillStyle = '#111'
  ctx.fillRect(wx - 18, wy - 16, 36, 10)
  ctx.beginPath()
  ctx.arc(wx, wy, 16, 0, Math.PI * 2)
  ctx.fillStyle = '#1C1C1C'
  ctx.fill()
  ctx.beginPath()
  ctx.arc(wx, wy, 8, 0, Math.PI * 2)
  ctx.fillStyle = '#E8E8E8'
  ctx.fill()
  ctx.beginPath()
  ctx.arc(wx, wy, 3, 0, Math.PI * 2)
  ctx.fillStyle = '#333'
  ctx.fill()
  ctx.fillStyle = '#E24A4A'
  ctx.fillRect(wx + 14, wy - 6, 3, 10)

  // rear jack
  ctx.fillStyle = '#8A7A62'
  ctx.fillRect(bodyX + bodyW - 6, bodyY + bodyH, 5, 14)

  ctx.fillStyle = '#F3EDE3'
  ctx.textAlign = 'left'
  ctx.font = `800 ${Math.max(11, w * 0.028)}px Inter, system-ui, sans-serif`
  ctx.fillText(props.name, bodyX, h * 0.045)
  ctx.fillStyle = '#9AA6B2'
  ctx.font = `600 ${Math.max(9, w * 0.018)}px Inter, system-ui, sans-serif`
  const inches = want.reduce((sum, bay) => sum + (Number(bay.inches) || 0), 0)
  const cap = props.capacityBbl || 110
  const lastIn = Number(want[3]?.inches || 0)
  const locked = lastIn >= COMMANDER_WEIR_INCHES - 0.05
  const pumping = !!want[3]?.pumping
  ctx.fillStyle = locked ? '#E7C36A' : pumping && want[3]?.mode === 'rate' ? '#E7C36A' : '#9AA6B2'
  ctx.fillText(
    locked
      ? `${(inches * COMMANDER_BBL_PER_INCH).toFixed(1)} / ${cap.toFixed(0)} BBL · EQUALIZED — keep last bay below 48 in`
      : pumping
        ? `${(inches * COMMANDER_BBL_PER_INCH).toFixed(1)} / ${cap.toFixed(0)} BBL · last bay ${Math.round(want[3]?.showInches || 0)} in · ${want[3]?.mode === 'rate' ? 'closed for rate' : 'filling / transferring 19–50 in'}`
        : `${(inches * COMMANDER_BBL_PER_INCH).toFixed(1)} / ${cap.toFixed(0)} BBL · keep last bay below 48 in`,
    bodyX,
    h * 0.085,
  )
}

function onClick(ev: MouseEvent) {
  const el = canvas.value
  if (!el) {
    emit('select', 3)
    return
  }
  const r = el.getBoundingClientRect()
  const x = ((ev.clientX - r.left) / r.width) * (el.clientWidth || props.widthPx)
  const y = ((ev.clientY - r.top) / r.height) * (el.clientHeight || Math.round(props.widthPx / 2.05))
  const hit = bayHits.find((b) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h)
  emit('select', hit?.i ?? 3)
}

watch(() => props.fillToken, (token) => {
  if (token !== lastToken) {
    lastToken = token
    replay = !props.live
    fillStart = performance.now()
  }
})

let io: IntersectionObserver | null = null
onMounted(() => {
  reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  replay = true
  fillStart = performance.now()
  io = new IntersectionObserver((entries) => {
    visible = entries.some((e) => e.isIntersecting)
    if (visible && !raf) raf = requestAnimationFrame(tick)
  }, { threshold: 0.05 })
  if (host.value) io.observe(host.value)
  raf = requestAnimationFrame(tick)
})

onBeforeUnmount(() => {
  io?.disconnect()
  cancelAnimationFrame(raf)
})
</script>

<style scoped>
.commander {
  aspect-ratio: 2.05;
  height: auto;
  max-width: 100%;
}
canvas {
  width: 100%;
  height: 100%;
  display: block;
  cursor: pointer;
}
</style>
